import React from 'react';
import { useField } from 'formik';
import styled, { css, CSSProperties } from 'styled-components/macro';
import { FormikErrorMessage } from './FormikErrorMessage';
import { FormikLabel } from './FormikLabel';
import { theme } from 'lib/style';
import { Flex, FlexProps } from '../styles/layout';

export const TextInput = styled.input<{ placeholderStyle?: PlaceholderProps }>`
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;
  color: #272a32;
  border-radius: 4px;
  height: 40px;
  width: 100%;
  box-sizing: border-box;
  border: solid 1px #e1e2e5;
  padding: 8px 12px;
  &:focus {
    outline: none;
    border: solid 1px ${theme.palette.covideoBlue100};
  }
  &:disabled {
    cursor: not-allowed;
  }

  &::placeholder {
    ${props =>
      props.placeholderStyle &&
      css`
        color: ${props.placeholderStyle.color};
        opacity: ${props.placeholderStyle.opacity};
      `}
`;

const CharCounter = styled.span`
  position: absolute;
  bottom: 8px;
  right: 12px;
  color: ${props => props.theme.colors.neutral[100]};
  pointer-events: none;
  font-weight: 400;
  font-size: 12px;
  line-height: 24px;
  letter-spacing: 0px;
  opacity: 0.6;
`;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

interface PlaceholderProps {
  color?: string;
  opacity?: number;
}

interface CustomInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label?: string;
  info?: string | React.ReactNode;
  isRequired: boolean;
  showCounter?: boolean;
  maxLength?: number;
  extendStyles?: {
    errorWrapper?: CSSProperties;
    labelStyles?: CSSProperties;
    asteriskStyles?: CSSProperties;
    inputWrapper?: FlexProps;
    inputStyles?: CSSProperties;
    mainWrapper?: FlexProps;
    placeholder?: PlaceholderProps;
  };
}

export const FormikInputField = React.forwardRef<
  HTMLInputElement,
  CustomInputProps
>(
  (
    { label, isRequired, extendStyles, info, maxLength, showCounter, ...props },
    ref
  ) => {
    const [field, meta] = useField(props);
    return (
      <Flex gap='4px' {...extendStyles?.mainWrapper}>
        {label && (
          <FormikLabel
            label={label}
            name={props.name}
            isRequired={isRequired}
            disabled={!!props.disabled}
            info={info}
            labelStyles={extendStyles?.labelStyles}
            asteriskStyles={extendStyles?.asteriskStyles}
          />
        )}
        <Flex {...extendStyles?.inputWrapper} gap='4px'>
          <Wrapper>
            <TextInput
              {...field}
              {...props}
              maxLength={maxLength}
              id={props.name}
              ref={ref}
              placeholderStyle={extendStyles?.placeholder}
              style={extendStyles?.inputStyles}
            />
            {showCounter && maxLength && (
              <CharCounter>
                {String(field.value).length}/{maxLength}
              </CharCounter>
            )}
          </Wrapper>
          <FormikErrorMessage meta={meta} extendStyles={extendStyles} />
        </Flex>
      </Flex>
    );
  }
);

FormikInputField.displayName = 'FormikInputField';
